import { keyToLabel } from "./utils/utils";
export type ToolIconName =
  | "document"
  | "edit"
  | "search"
  | "database"
  | "book"
  | "list"
  | "table"
  | "bot"
  | "wrench";

type ToolPresentation = {
  icon: ToolIconName;
  singular: string;
  plural: (count: number) => string;
  idle: string;
  running: string;
  complete: string;
  targetArg?: string[];
};

const same = (phrase: string) => () => phrase;

export const TOOL_PRESENTATION: Record<string, ToolPresentation> = {
  list_quality_event_definitions: {
    icon: "list",
    singular: "listed quality event definitions",
    plural: same("listed quality event definitions"),
    idle: "List quality event definitions",
    running: "Listing quality event definitions",
    complete: "Listed quality event definitions",
  },
  read_quality_event_definition: {
    icon: "book",
    singular: "read quality event definition",
    plural: same("read quality event definition"),
    idle: "Read quality event definition",
    running: "Reading quality event definition",
    complete: "Read quality event definition",
  },
  list_quality_event_records: {
    icon: "list",
    singular: "listed quality event records",
    plural: same("listed quality event records"),
    idle: "List quality event records",
    running: "Listing quality event records",
    complete: "Listed quality event records",
  },
  read_quality_event_record: {
    icon: "database",
    singular: "read quality event record",
    plural: same("read quality event record"),
    idle: "Read quality event record",
    running: "Reading quality event record",
    complete: "Read quality event record",
  },
  read_quality_event_record_activity: {
    icon: "list",
    singular: "read quality event record activity",
    plural: same("read quality event record activity"),
    idle: "Read quality event record activity",
    running: "Reading quality event record activity",
    complete: "Read quality event record activity",
  },
  list_quality_event_files: {
    icon: "list",
    singular: "listed quality event evidence files",
    plural: same("listed quality event evidence files"),
    idle: "List quality event evidence files",
    running: "Listing quality event evidence files",
    complete: "Listed quality event evidence files",
  },
  list_quality_event_document_references: {
    icon: "list",
    singular: "listed quality event document references",
    plural: same("listed quality event document references"),
    idle: "List quality event document references",
    running: "Listing quality event document references",
    complete: "Listed quality event document references",
  },
  create_quality_event_record: {
    icon: "edit",
    singular: "created quality event record",
    plural: same("created quality event record"),
    idle: "Create quality event record",
    running: "Creating quality event record",
    complete: "Created quality event record",
  },
  update_quality_event_record: {
    icon: "edit",
    singular: "updated quality event record",
    plural: same("updated quality event record"),
    idle: "Update quality event record",
    running: "Updating quality event record",
    complete: "Updated quality event record",
  },
  edit_quality_event_record: {
    icon: "edit",
    singular: "edited quality event record",
    plural: same("edited quality event record"),
    idle: "Edit quality event record",
    running: "Editing quality event record",
    complete: "Edited quality event record",
  },
  list_documents: {
    icon: "list",
    singular: "listed documents",
    plural: same("listed documents"),
    idle: "List documents",
    running: "Listing documents",
    complete: "Listed documents",
  },
  list_data_keys: {
    icon: "list",
    singular: "listed data keys",
    plural: same("listed data keys"),
    idle: "List data keys",
    running: "Listing data keys",
    complete: "Listed data keys",
  },
  read_data_key: {
    icon: "database",
    singular: "read a data key",
    plural: (n) => `read ${n} data keys`,
    idle: "Read data key",
    running: "Reading data key",
    complete: "Read data key",
    targetArg: ["data_key", "id"],
  },
  read_data_key_configuration: {
    icon: "database",
    singular: "read data key configuration",
    plural: same("read data key configuration"),
    idle: "Read data key configuration",
    running: "Reading data key configuration",
    complete: "Read data key configuration",
    targetArg: ["id"],
  },
  edit_data_key: {
    icon: "database",
    singular: "edited a data key",
    plural: (n) => `edited ${n} data keys`,
    idle: "Edit data key",
    running: "Editing data key",
    complete: "Edited data key",
    targetArg: ["id"],
  },
  write_data_key: {
    icon: "database",
    singular: "edited a data key",
    plural: (n) => `edited ${n} data keys`,
    idle: "Edit data key",
    running: "Editing data key",
    complete: "Edited data key",
    targetArg: ["id"],
  },
  read_document: {
    icon: "document",
    singular: "read a document",
    plural: (n) => `read ${n} documents`,
    idle: "Read",
    running: "Reading",
    complete: "Read",
    targetArg: ["id", "document_id"],
  },
  read_document_metadata: {
    icon: "document",
    singular: "read document metadata",
    plural: same("read document metadata"),
    idle: "Read metadata for",
    running: "Reading metadata for",
    complete: "Read metadata for",
    targetArg: ["id", "document_id"],
  },
  edit_document: {
    icon: "edit",
    singular: "edited a document",
    plural: (n) => `edited ${n} documents`,
    idle: "Edit document",
    running: "Editing document",
    complete: "Edited",
    targetArg: ["document_id", "id"],
  },
  edit_custom_document_text: {
    icon: "edit",
    singular: "edited a document",
    plural: (n) => `edited ${n} documents`,
    idle: "Edit document",
    running: "Editing document",
    complete: "Edited",
    targetArg: ["document_id"],
  },
  grep_search_standards: {
    icon: "book",
    singular: "searched standards",
    plural: same("searched standards"),
    idle: "Search standards for",
    running: "Searching standards for",
    complete: "Searched standards for",
    targetArg: ["query", "pattern"],
  },
  read_standard: {
    icon: "book",
    singular: "read a standard",
    plural: (n) => `read ${n} standards`,
    idle: "Read",
    running: "Reading",
    complete: "Read",
    targetArg: ["id"],
  },
  list_standards: {
    icon: "list",
    singular: "listed standards",
    plural: same("listed standards"),
    idle: "List standards",
    running: "Listing standards",
    complete: "Listed standards",
  },
  grep_search_documents: {
    icon: "search",
    singular: "searched documents",
    plural: same("searched documents"),
    idle: "Search documents for",
    running: "Searching documents for",
    complete: "Searched documents for",
    targetArg: ["pattern", "query"],
  },
  grep_search: {
    icon: "search",
    singular: "searched documents",
    plural: same("searched documents"),
    idle: "Search for",
    running: "Searching for",
    complete: "Searched for",
    targetArg: ["pattern", "query"],
  },
  create_document: {
    icon: "document",
    singular: "created a document",
    plural: (n) => `created ${n} documents`,
    idle: "Create document",
    running: "Creating document",
    complete: "Created document",
    targetArg: ["default_title"],
  },
  create_document_version: {
    icon: "document",
    singular: "created a document version",
    plural: (n) => `created ${n} document versions`,
    idle: "Create document version",
    running: "Creating document version",
    complete: "Created document version",
    targetArg: ["document_id"],
  },
  search_web: {
    icon: "search",
    singular: "searched the web",
    plural: same("searched the web"),
    idle: "Search web for",
    running: "Searching web for",
    complete: "Searched web for",
    targetArg: ["query"],
  },
  get_device: {
    icon: "document",
    singular: "read device information",
    plural: same("read device information"),
    idle: "Read device information",
    running: "Reading device information",
    complete: "Read device information",
  },
  task: {
    icon: "bot",
    singular: "delegated to a sub-agent",
    plural: (n) => `delegated to ${n} sub-agents`,
    idle: "Delegated to",
    running: "Delegating to",
    complete: "Delegated to",
    targetArg: ["subagent_type"],
  },
  draft_skill: {
    icon: "document",
    singular: "drafted a skill",
    plural: (n) => `drafted ${n} skills`,
    idle: "Draft skill",
    running: "Drafting skill",
    complete: "Drafted skill",
    targetArg: ["name"],
  },
  draft_training_quiz: {
    icon: "document",
    singular: "drafted a quiz",
    plural: (n) => `drafted ${n} quizzes`,
    idle: "Draft quiz",
    running: "Drafting quiz",
    complete: "Drafted quiz",
  },
  draft_training_program: {
    icon: "document",
    singular: "drafted a training program",
    plural: (n) => `drafted ${n} training programs`,
    idle: "Draft training program",
    running: "Drafting training program",
    complete: "Drafted training program",
  },
  write_todos: {
    icon: "list",
    singular: "updated todos",
    plural: same("updated todos"),
    idle: "Update todos",
    running: "Updating todos",
    complete: "Updated todos",
  },
  add_user_to_functional_group: {
    icon: "wrench",
    singular: "updated a functional group",
    plural: (n) => `updated ${n} functional groups`,
    idle: "Join functional group",
    running: "Joining functional group",
    complete: "Joined functional group",
  },
};

const KNOWN_SERVERS: Record<string, string> = {
  linear: "Linear",
  github: "GitHub",
  jira: "Jira",
  slack: "Slack",
  pubmed: "PubMed",
  google: "Google Drive",
  fda: "FDA",
};

export const getToolServerName = (
  name: string,
  connectionNames: string[] = []
) => {
  const prefix = name.split("_")[0];
  if (KNOWN_SERVERS[prefix]) return KNOWN_SERVERS[prefix];
  return connectionNames.find((connectionName) => {
    const slug = connectionName
      .toLowerCase()
      .replace(/[^a-z0-9_]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 40);
    return name.startsWith(`${slug}_`);
  });
};

export const getToolGroupSummary = (
  names: string[],
  connectionNames: string[] = []
) => {
  const counts = new Map<string, { count: number; name: string }>();
  names.forEach((name) => {
    const server = getToolServerName(name, connectionNames);
    const key = TOOL_PRESENTATION[name] ? name : `unknown:${server ?? ""}`;
    const existing = counts.get(key);
    counts.set(key, { count: (existing?.count ?? 0) + 1, name });
  });
  const phrases = [...counts.values()].map(({ name, count }) => {
    const config = TOOL_PRESENTATION[name];
    if (config) return count === 1 ? config.singular : config.plural(count);
    const server = getToolServerName(name, connectionNames);
    return `used ${count === 1 ? "a" : count} ${server ? `${server} ` : ""}tool${count === 1 ? "" : "s"}`;
  });
  const rest = phrases.length > 3 ? ` + ${phrases.length - 3} more` : "";
  return `Used ${names.length} ${names.length === 1 ? "tool" : "tools"} — ${phrases.slice(0, 3).join(", ")}${rest}`;
};

export const parseToolArgs = (raw: unknown): Record<string, unknown> => {
  let args = raw;
  if (typeof args === "string") {
    try {
      args = JSON.parse(args);
    } catch {
      return {};
    }
  }
  if (!args || typeof args !== "object" || Array.isArray(args)) return {};
  const values = args as Record<string, unknown>;
  if (typeof values.input === "string") {
    try {
      const nested = JSON.parse(values.input);
      if (nested && typeof nested === "object" && !Array.isArray(nested))
        return { ...values, ...nested };
    } catch {
      /* Streaming may leave input JSON unfinished. */
    }
  }
  return values;
};

export const getToolTarget = (name: string, args: Record<string, unknown>) => {
  const keys = TOOL_PRESENTATION[name]?.targetArg ?? [];
  for (const key of keys) {
    const value = args[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
};

export const getToolRowLabel = (
  name: string,
  args: Record<string, unknown>,
  running: boolean,
  complete = false,
  connectionNames: string[] = []
) => {
  const config = TOOL_PRESENTATION[name];
  if (!config) {
    const server = getToolServerName(name, connectionNames);
    return `${running ? "Using" : "Used"} ${server ? `${server} ` : ""}tool ${name}`;
  }
  const verb = running
    ? config.running
    : complete
      ? config.complete
      : config.idle;
  const rawTarget = getToolTarget(name, args);
  const target =
    name === "task" ? keyToLabel(rawTarget) || "subagent" : rawTarget;
  if (!target) return verb;
  return `${verb} ${name.startsWith("grep_search") || name === "search_web" ? `"${target}"` : target}`;
};
